// Originally taken from https://github.com/mapbox/polylabel/blob/master/polylabel.js
import RBush from "rbush";
import Queue from "tinyqueue";
/** Find the furthest location from the edges of a given polygon. Polygon must be simple (i.e.
 * not multiple shapes), have no holes. However, it may have concavities.
 *
 * Note: `precision` is a required parameter to force the caller to make a proactive decision,
 * since choosing a too-precise value can have catastrophic implications for performance.
 * Don't choose a value below what you need for the use case.
 */
export default function polylabelFast(polygon, precision) {
    // find the bounding box of the outer ring
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const point of polygon[0]) {
        if (point.x < minX)
            minX = point.x;
        if (point.y < minY)
            minY = point.y;
        if (point.x > maxX)
            maxX = point.x;
        if (point.y > maxY)
            maxY = point.y;
    }
    // Index the polygon with an R-tree
    const edges = [];
    for (const ring of polygon) {
        for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
            const a = ring[i];
            const b = ring[j];
            edges.push({
                minX: Math.min(a.x, b.x),
                minY: Math.min(a.y, b.y),
                maxX: Math.max(a.x, b.x),
                maxY: Math.max(a.y, b.y),
                a,
                b,
            });
        }
    }
    const tree = new RBush();
    tree.load(edges);
    const width = maxX - minX;
    const height = maxY - minY;
    const cellSize = Math.max(precision, Math.min(width, height));
    if (cellSize === precision) {
        return { x: minX, y: minY, distance: 0 };
    }
    // a priority queue of cells in order of their "potential" (max distance to polygon)
    const cellQueue = new Queue([], (a, b) => b.max - a.max);
    // take centroid as the first best guess
    let bestCell = getCentroidCell(polygon, tree);
    // second guess: bounding box centroid
    const bboxCell = new Cell({ x: minX + width / 2, y: minY + height / 2 }, 0, tree);
    if (bboxCell.d > bestCell.d)
        bestCell = bboxCell;
    function potentiallyQueue(pos, h) {
        const cell = new Cell(pos, h, tree);
        if (cell.max > bestCell.d + precision)
            cellQueue.push(cell);
        // update the best cell if we found a better one
        if (cell.d > bestCell.d) {
            bestCell = cell;
        }
    }
    // cover polygon with initial cells
    let h = cellSize / 2;
    for (let x = minX; x < maxX; x += cellSize) {
        for (let y = minY; y < maxY; y += cellSize) {
            potentiallyQueue({ x: x + h, y: y + h }, h);
        }
    }
    while (cellQueue.length) {
        // pick the most promising cell from the queue
        const cell = cellQueue.pop();
        const pos = cell.pos;
        // do not drill down further if there's no chance of a better solution
        if (cell.max - bestCell.d <= precision)
            break;
        // split the cell into four cells
        h = cell.h / 2;
        potentiallyQueue({ x: pos.x - h, y: pos.y - h }, h);
        potentiallyQueue({ x: pos.x + h, y: pos.y - h }, h);
        potentiallyQueue({ x: pos.x - h, y: pos.y + h }, h);
        potentiallyQueue({ x: pos.x + h, y: pos.y + h }, h);
    }
    return { ...bestCell.pos, distance: bestCell.d };
}
class Cell {
    pos;
    h;
    d;
    max;
    constructor(pos, h, tree) {
        this.pos = pos;
        this.h = h;
        this.d = pointToPolygonDist(pos, tree); // distance from cell center to polygon
        this.max = this.d + this.h * Math.SQRT2; // max distance to polygon within a cell
    }
}
// signed distance from point to polygon outline (negative if point is outside)
function pointToPolygonDist(point, tree) {
    let inside = false;
    const foundEdges = tree.search({
        minX: point.x,
        minY: point.y,
        maxX: Infinity,
        maxY: point.y,
    });
    for (const edge of foundEdges) {
        const a = edge.a;
        const b = edge.b;
        if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
            inside = !inside;
        }
    }
    const minDistSq = distToClosestEdgeSq(point, tree);
    return (inside ? 1 : -1) * Math.sqrt(minDistSq);
}
function distToClosestEdgeSq(point, tree) {
    const queue = new Queue(undefined, (a, b) => a.dist - b.dist);
    let node = tree.data;
    // search through the segment R-tree with a depth-first search using a priority queue
    // in the order of distance to the point
    while (node) {
        for (const child of node.children) {
            const dist = node.leaf ? getSegDistSq(point, child.a, child.b) : getBoxDistSq(point, child);
            queue.push({
                node: child,
                dist: dist,
            });
        }
        while (queue.length && !queue.peek().node.children) {
            return queue.pop().dist;
        }
        node = queue.pop();
        if (node)
            node = node.node;
    }
    throw new Error("Error in polylabel");
}
function getBoxDistSq(point, box) {
    const dx = axisDist(point.x, box.minX, box.maxX);
    const dy = axisDist(point.y, box.minY, box.maxY);
    return dx * dx + dy * dy;
}
function axisDist(k, min, max) {
    return k < min ? min - k : k <= max ? 0 : k - max;
}
// get polygon centroid
function getCentroidCell(polygon, tree) {
    let area = 0;
    let x = 0;
    let y = 0;
    const points = polygon[0];
    for (let i = 0, len = points.length, j = len - 1; i < len; j = i++) {
        const a = points[i];
        const b = points[j];
        const f = a.x * b.y - b.x * a.y;
        x += (a.x + b.x) * f;
        y += (a.y + b.y) * f;
        area += f * 3;
    }
    const centroid = new Cell({ x: x / area, y: y / area }, 0, tree);
    if (area === 0 || centroid.d < 0)
        return new Cell(points[0], 0, tree);
    return centroid;
}
// get squared distance from a point to a segment
function getSegDistSq(point, a, b) {
    let x = a.x;
    let y = a.y;
    let dx = b.x - x;
    let dy = b.y - y;
    if (dx !== 0 || dy !== 0) {
        const t = ((point.x - x) * dx + (point.y - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) {
            x = b.x;
            y = b.y;
        }
        else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }
    dx = point.x - x;
    dy = point.y - y;
    return dx * dx + dy * dy;
}
//# sourceMappingURL=polylabel.js.map